import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ImageUp, Trash, Check, UserCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { useAuth } from "@/hooks/auth";
import {
  getMyEmployeeProfile,
  updateMyEmployeeProfile,
} from "@/Api/employeeProfile";
import {
  AddressFieldsStep,
  addressFieldsSchema,
} from "@/components/AddressFieldsStep";
import {
  PersonalDataStep,
  personalDataSchema,
  PERSONAL_DATA_OMIT_WHEN_EMPTY,
} from "@/components/PersonalDataStep";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";

const schema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  email: z.string({ message: "*requerido" }).email("*email inválido"),
  photo: z.any().optional(),
  ...addressFieldsSchema.shape,
  ...personalDataSchema.shape,
});

type SchemaType = z.infer<typeof schema>;

const OMIT_WHEN_EMPTY = [...PERSONAL_DATA_OMIT_WHEN_EMPTY];

export const ProfileCompletionModal = () => {
  const { user } = useAuth();
  const hasEmployee = !!user?.employee;
  const { toast } = useToastNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState<string | null>(null);

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: { photo: null },
  });

  const populateForm = (profile: any) => {
    form.setValue("name", profile?.name || "");
    form.setValue("email", profile?.email || "");
    setCurrentPhotoUrl(profile?.photo || null);
    form.setValue("zip_code", profile?.address?.zip_code || "");
    form.setValue("street", profile?.address?.street || "");
    form.setValue("number", profile?.address?.number || "");
    form.setValue("neighborhood", profile?.address?.neighborhood || "");
    form.setValue("complement", profile?.address?.complement || "");
    form.setValue("state", profile?.address?.state || "");
    form.setValue("city", profile?.address?.city || "");
    form.setValue("father_name", profile?.father_name || "");
    form.setValue("mother_name", profile?.mother_name || "");
    form.setValue("birth_date", profile?.birth_date?.slice(0, 10) || "");
    form.setValue("birthplace", profile?.birthplace || "");
    form.setValue("birthplace_state", profile?.birthplace_state || "");
    form.setValue("nationality", profile?.nationality || "");
    form.setValue(
      "gender",
      profile?.gender != null ? String(profile.gender) : ""
    );
    form.setValue(
      "marital_status",
      profile?.marital_status != null ? String(profile.marital_status) : ""
    );
    form.setValue(
      "ethnicity",
      profile?.ethnicity != null ? String(profile.ethnicity) : ""
    );
    form.setValue(
      "education_level",
      profile?.education_level != null ? String(profile.education_level) : ""
    );
    form.setValue("mobile_phone", profile?.mobile_phone || "");
  };

  useEffect(() => {
    if (!hasEmployee) {
      setIsLoading(false);
      return;
    }

    const load = async () => {
      const { data } = await getMyEmployeeProfile();
      const profile = data?.response;

      if (!profile || profile.completion?.is_complete) {
        setIsLoading(false);
        return;
      }

      populateForm(profile);
      setIsLoading(false);
      setIsOpen(true);
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasEmployee]);

  const formWatch = form.watch();

  const formSubmit = async (data: SchemaType) => {
    setSubmitting(true);

    const payload: Record<string, any> = {};
    Object.keys(data).forEach((key) => {
      if (key === "photo") {
        if (data.photo?.[0]) payload.photo = data.photo[0];
        return;
      }
      if (OMIT_WHEN_EMPTY.includes(key) && !data[key as keyof SchemaType]) {
        return;
      }
      payload[key] = data[key as keyof SchemaType];
    });

    const { data: apiResponse, ok } = await updateMyEmployeeProfile(payload);
    setSubmitting(false);

    if (!ok) {
      toast({
        description: apiResponse?.message || "Erro ao salvar seus dados",
        variant: "destructive",
      });
      return;
    }

    const profile = apiResponse?.response;
    toast({
      description: profile?.completion?.is_complete
        ? "Perfil completo, obrigado!"
        : "Dados salvos!",
      action: <Check color="#1edc2b" />,
    });

    form.setValue("photo", null);
    setIsOpen(false);
  };

  if (!hasEmployee || isLoading) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Complete seu cadastro
          </DialogDescription>
          <DialogTitle className="text-2xl">
            Precisamos de mais alguns dados
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[calc(90vh-14rem)] pr-4">
          <Form
            onSubmit={form.handleSubmit(formSubmit)}
            {...form}
            className="space-y-4 p-2"
          >
            <Form.Row>
              <Form.Field name="photo">
                {({ field }) => (
                  <Form.Item className="flex flex-col items-center gap-1 w-full">
                    <div className="flex items-center rounded-full size-28 border border-dashed border-gray-900/25 dark:border-gray-500 relative">
                      <div className="flex text-sm/6 text-gray-600 h-full w-full">
                        <label
                          htmlFor="profile-completion-photo-upload"
                          className="w-full h-full flex items-center justify-center cursor-pointer font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 group rounded-full overflow-hidden"
                        >
                          {formWatch?.photo?.[0] ? (
                            <img
                              className="absolute top-0 left-0 aspect-square object-contain w-full h-full -z-1 group-hover:brightness-75 transition rounded-full"
                              src={URL.createObjectURL(formWatch.photo[0])}
                              alt="profile_image"
                            />
                          ) : (
                            currentPhotoUrl && (
                              <img
                                className="absolute top-0 left-0 aspect-square object-contain w-full h-full -z-1 group-hover:brightness-75 transition rounded-full"
                                src={currentPhotoUrl}
                                alt="profile_image"
                              />
                            )
                          )}
                          <span className="flex gap-2 items-center relative z-50">
                            {!formWatch?.photo?.[0] && !currentPhotoUrl ? (
                              <ImageUp className="size-6 group-hover:text-blue-700" />
                            ) : (
                              <ImageUp className="size-6 opacity-0 transition group-hover:opacity-100" />
                            )}
                          </span>
                          <input
                            id="profile-completion-photo-upload"
                            type="file"
                            className="sr-only"
                            accept="image/*"
                            onChange={(e) => {
                              const files = e.target.files;
                              if (files?.length) field.onChange(files);
                            }}
                          />
                        </label>
                      </div>
                    </div>
                    {formWatch?.photo?.[0] && (
                      <Button
                        type="button"
                        className="h-6 px-0 bg-transparent text-slate font-medium hover:text-red-600 hover:bg-transparent flex gap-1"
                        onClick={() => form.setValue("photo", null)}
                      >
                        <Trash className="size-4" />
                        Remover
                      </Button>
                    )}
                    {!currentPhotoUrl && !formWatch?.photo?.[0] && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <UserCircle className="size-3" /> Foto de perfil
                      </span>
                    )}
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>

            <Form.Row>
              <Form.Field name="name">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Nome <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} placeholder="Seu nome" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
              <Form.Field name="email">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Email <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} placeholder="Seu email" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>

            <p className="text-sm font-medium text-muted-foreground pt-2">
              Endereço
            </p>
            <AddressFieldsStep />

            <p className="text-sm font-medium text-muted-foreground pt-2">
              Dados Pessoais
            </p>
            <PersonalDataStep />

            <DialogFooter className="flex-row justify-between sm:justify-between gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsOpen(false)}
              >
                Pular por agora
              </Button>
              <Button
                type="submit"
                className={cn(colors.bg_primary)}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    Salvando <LoaderSpin />
                  </>
                ) : (
                  "Salvar"
                )}
              </Button>
            </DialogFooter>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
